import { Combobox, type ComboboxItem, type OptionValue, Search } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useKmDictionaries } from '@/entities/ontology';
import type { KmEntitiesFilterKey } from '../../../model/entitiesFilter';
import { KmRelatedEntitiesFilter } from './KmRelatedEntitiesFilter';

type Props = {
  /** Выбранные домены (коды) */
  domain: string[];
  /** Выбранные типы сущностей (коды) */
  type: string[];
  /** Выбранные связанные сущности (идентификаторы) */
  related: number[];
  /** Строка поиска по значениям атрибутов */
  search: string;
  /** Изменение любого условия отбора; пустой набор — условие снято */
  onChange: (key: KmEntitiesFilterKey, value: string | string[]) => void;
};

// Опции справочника: подпись — название, значение — код
const toOptions = (items: { code: string; name: string }[]): ComboboxItem[] =>
  items.map((item) => ({ value: item.code, label: item.name }));

/**
 * Панель над списком: поиск по значениям атрибутов и фильтры по домену, типу сущности
 * и связанным сущностям — каждый с выбором нескольких значений.
 */
export const KmEntitiesFilters: FC<Props> = ({ domain, type, related, search, onChange }) => {
  const { t } = useTranslation(['common', 'kmEntities']);

  const { entityTypes, domains } = useKmDictionaries();

  const domainOptions = useMemo(() => toOptions(domains), [domains]);
  const typeOptions = useMemo(() => toOptions(entityTypes), [entityTypes]);

  const handleSelect = (key: KmEntitiesFilterKey) => (values: OptionValue[]) => onChange(key, values.map(String));

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <Search
        className="flex-1"
        value={search}
        onChange={(e) => onChange('search', e.target.value)}
        placeholder={t('kmEntities:searchPlaceholder')}
      />
      <Combobox
        isMulti
        className="sm:w-56"
        options={domainOptions}
        value={domain}
        onChange={handleSelect('domain')}
        placeholder={t('kmEntities:domain')}
      />
      <Combobox
        isMulti
        className="sm:w-56"
        options={typeOptions}
        value={type}
        onChange={handleSelect('type')}
        placeholder={t('kmEntities:entityType')}
      />
      <KmRelatedEntitiesFilter value={related} onChange={(values) => onChange('related', values)} />
    </div>
  );
};
